/**
 * O avisador da resposta do atendente — D3 (passo 19, PR 2), irmão do `avisoDaMensagem.ts` (D2). Fora do React e testável sem
 * componente; o `aoVivoDoPortal` o chama no `atendimento:mensagem_nova`.
 *
 * O servidor decide o nível do parceiro para `portal_atendimento` e o manda no evento como `aviso`: `som` → aviso na tela + som;
 * `aviso` → só o aviso; `sino` ("Só no contador do Atendimento") → nada na tela (só o número no menu e na conversa). Quem cala o
 * aviso aqui, nesta ordem:
 *   1. a conversa está à vista (aberta e aba visível): o parceiro está lendo;
 *   2. o nível não é `som`/`aviso`;
 *   3. (só o som) interruptor do aparelho, esta aba toca, a conversa não tocou há 30 s e a janela global de 5 s está livre.
 *
 * ⚠️ A conversa só "gasta" a janela de 30 s quando o som SAI. A aba que não toca e a janela global ocupada não gastam: senão a aba
 *    errada, ou o som de outra conversa, calaria esta por meio minuto sem ninguém ter ouvido nada.
 * O som é sempre o "plim": uma resposta de atendimento nunca é crítica.
 */
import { tocarSomDeAviso } from "@/lib/somDeAviso";
import { reservarJanelaDoSom } from "./janelaDoSom";
import { salaAbertaAVista } from "./salaAberta";
import { somDoAparelhoLigado } from "./somDoAparelho";
import { contarAvisoNoTitulo } from "./tituloDaAba";
import { estaAbaToca } from "./travaDoSom";

/** O que o avisador lê do `atendimento:mensagem_nova` (o evento traz mais; cabe por estrutura). */
export interface RespostaDoAtendimento {
  session_id: string;
  aviso?: string | null;
}

/** No máximo 1 som por conversa a cada 30 s: uma rajada de respostas toca uma vez. */
export const JANELA_DO_ATENDIMENTO_MS = 30_000;

/** Quando saiu o último som de cada sessão (memória desta aba; some no recarregar). */
const ultimoSomPorSessao = new Map<string, number>();

function sessaoLivre(sessionId: string, agora: number): boolean {
  const ultimo = ultimoSomPorSessao.get(sessionId);
  if (ultimo === undefined) return true;
  // Marca no futuro (relógio mexido para trás) não pode calar a conversa até o relógio voltar.
  if (ultimo > agora) return true;
  return agora - ultimo >= JANELA_DO_ATENDIMENTO_MS;
}

/**
 * Decide, mostra (pela função recebida), conta no título e toca — nesta ordem.
 * `mostrou` só é verdadeiro se o aviso na tela de fato saiu; `tocou`, se o som saiu.
 */
export function anunciarAtendimento(
  r: RespostaDoAtendimento,
  mostrar: () => void,
  agora: number = Date.now()
): { mostrou: boolean; tocou: boolean } {
  const resultado = { mostrou: false, tocou: false };
  // A conversa que o parceiro está lendo não avisa nem toca: a resposta já está na tela dele (e a busca a marca como lida).
  if (salaAbertaAVista(r.session_id)) return resultado;
  if (r.aviso !== "som" && r.aviso !== "aviso") return resultado;

  try {
    mostrar();
    resultado.mostrou = true;
    contarAvisoNoTitulo();
  } catch (erro) {
    // O aviso na tela que falha não cala o som (o número do menu já contou).
    console.warn("[avisos] o aviso da resposta na tela falhou:", erro);
  }

  // ⚠️ A ordem importa: cada condição barata e sem efeito colateral vem antes de `reservarJanelaDoSom`, que grava a janela
  //    global; e a conversa só é marcada depois dela.
  if (
    r.aviso === "som" &&
    somDoAparelhoLigado() &&
    estaAbaToca() &&
    sessaoLivre(r.session_id, agora) &&
    reservarJanelaDoSom()
  ) {
    // `tocarSomDeAviso` devolve se agendou o som (áudio ainda bloqueado → `false`): a conversa só gasta a janela de 30 s quando
    // o som SAI. Isso não devolve a janela GLOBAL de 5 s: `reservarJanelaDoSom` já a gravou.
    resultado.tocou = tocarSomDeAviso("aviso");
    if (resultado.tocou) ultimoSomPorSessao.set(r.session_id, agora);
  }
  return resultado;
}

/** Só para os testes. */
export function _reiniciarAvisoDoAtendimentoParaTeste(): void {
  ultimoSomPorSessao.clear();
}
